import { AuthnTransaction as OktaAuthTransaction } from '@okta/okta-auth-js';

export enum LoginStatus {
  SUCCESS = 'SUCCESS',
  MFA_REQUIRED = 'MFA_REQUIRED',
  MFA_CHALLENGE = 'MFA_CHALLENGE',
  LOCKED_OUT = 'LOCKED_OUT',
  MFA_ENROLL = 'MFA_ENROLL',
  MFA_ENROLL_ACTIVATE = 'MFA_ENROLL_ACTIVATE',
}

export enum ViewScreen {
  Login,
  LoginSuccess,
  MFA,
  MfaChallenge,
  MfaEnrollWelcome,
  MfaEnrollVerify,
  MfaEnrollPhone,
  MfaEnrollPhoneConfirm,
  InviteFamily,
}

export const VIEW_SCREEN_URL: Record<ViewScreen, string> = {
  [ViewScreen.Login]: '/public/login',
  [ViewScreen.LoginSuccess]: '/public/login/callback',
  [ViewScreen.InviteFamily]: '/secure/onboarding/invite-family',
  [ViewScreen.MFA]: '/public/login/mfa',
  [ViewScreen.MfaChallenge]: '/public/login/mfa/challenge',
  [ViewScreen.MfaEnrollWelcome]: '/public/login/enroll',
  [ViewScreen.MfaEnrollVerify]: '/public/login/enroll/challenge',
  [ViewScreen.MfaEnrollPhone]: '/public/login/enroll/phone-number',
  [ViewScreen.MfaEnrollPhoneConfirm]:
    '/public/login/enroll/phone-number-confirm',
};

export type FactorType = 'sms' | 'email';
export type FactorEnrollStatus = 'NOT_SETUP' | 'ACTIVE';

export interface AuthTransaction extends OktaAuthTransaction {
  cancel: () => Promise<AuthTransaction>;
  prev: () => Promise<AuthTransaction>;
}

export interface AuthTransactionSuccess extends AuthTransaction {
  status: LoginStatus.SUCCESS;
  sessionToken: string;
}

export interface AuthTransactionMfaChallenge extends AuthTransaction {
  status: LoginStatus.MFA_CHALLENGE;
  factor: Factor;
  verify: ({
    passCode,
    rememberDevice,
  }: {
    passCode: string;
    rememberDevice: boolean;
  }) => Promise<AuthTransaction>;
  resend: (string: FactorType) => Promise<AuthTransaction>;
}

export interface AuthTransactionMfaEnroll extends AuthTransaction {
  status: LoginStatus.MFA_ENROLL;
  factors: FactorEnroll[];
  skip: () => Promise<AuthTransaction>;
}

export interface AuthTransactionMfaEnrollActivate extends AuthTransaction {
  status: LoginStatus.MFA_ENROLL_ACTIVATE;
  factor: FactorEnroll;
  activate: ({ passCode }: { passCode: string }) => Promise<AuthTransaction>;
  resend: () => Promise<AuthTransaction>;
}

export interface AuthTransactionMfaEnrollSuccess extends AuthTransaction {
  status: LoginStatus.SUCCESS;
  skip: () => Promise<AuthTransaction>;
}

export interface AuthTransactionMfaRequired extends AuthTransaction {
  status: LoginStatus.MFA_REQUIRED;
  factors: FactorVerify[];
}

export interface Factor {
  factorType: FactorType;
  id: string;
  profile: {
    email?: string;
    phoneNumber?: string;
  };
}

export interface FactorVerify extends Factor {
  verify: () => Promise<AuthTransaction>;
}

export interface FactorEnroll extends Factor {
  enroll: (profile?: Profile) => Promise<AuthTransaction>;
  status: FactorEnrollStatus;
}

interface Profile {
  profile: {
    phoneNumber: string;
    updatePhone: boolean;
  };
}

export enum SignInFailReason {
  Unknown = 0,
  InvalidCredentials = 1,
  LockedOut = 2,
  VerifyFailed = 3,
  TokenExpired = 4,
  NotFound = 5,
  SmsLimit = 6,
  SmsAlreadySent = 7,
}

export type LogoutVariations = 'logout' | 'inactive';
